import {
  exchangeGroupSchema,
  exchangeVariantSchema,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import type { Step } from "@redotech/redo-return-core-common/return-flow";
import {
  ReturnOptionMethodZodSchema,
  ReturnOptionZodSchema,
} from "@redotech/redo-return-core-common/return-flow-schema";
import { ReturnOptionType } from "@redotech/redo-return-operations-common/return-flow/return-step-options";
import * as z from "zod";

const FlowAnswerSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("multiple_choice"),
    optionIndex: z.number().int().nonnegative(),
  }),
  z.object({ type: z.literal("input"), values: z.array(z.string()) }),
  z.object({ type: z.literal("acknowledge") }),
  z.object({
    type: z.literal("ai_return_reasons"),
    isDamaged: z.boolean(),
    isWrongProduct: z.boolean(),
    isExchange: z.boolean(),
    matchedCustomGuidanceId: z.string().optional(),
  }),
]);

/**
 * Server-computed RETURN options (same-item / variant / group / advanced
 * exchange, shop-or-return). Plain JSON — no Temporal — safe over rpc-light.
 */
const ReturnStepPageOptionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(ReturnOptionType.SHOP_OR_RETURN),
    options: z.array(ReturnOptionZodSchema),
  }),
  z.object({
    type: z.literal(ReturnOptionType.EXCHANGE_GROUPS),
    eligibleExchangeGroups: z.array(exchangeGroupSchema),
  }),
  z.object({
    type: z.literal(ReturnOptionType.VARIANT_EXCHANGE),
    returnOption: ReturnOptionZodSchema,
  }),
  z.object({
    type: z.literal(ReturnOptionType.SAME_ITEM_EXCHANGE),
    variant: exchangeVariantSchema,
    returnOption: ReturnOptionZodSchema,
  }),
  z.object({
    type: z.literal(ReturnOptionType.BUNDLE_TYPE),
    options: z.array(ReturnOptionZodSchema),
  }),
  z.object({ type: z.literal(ReturnOptionType.ADVANCED_EXCHANGE) }),
]);

/** Flow-step indices traversed to reach this view (hops included). */
const flowPathField = z.array(z.number().int());

const aiCustomGuidanceSchema = z.object({
  id: z.string(),
  description: z.string(),
  next: z.number().int(),
});

/** Projection of Step.SubmitClaim["option"]; parse() also strips unread fields. */
export const SubmitClaimOptionViewSchema = z.object({
  method: ReturnOptionMethodZodSchema,
  variantExchangeAddOn: z.boolean().optional(),
  variantExchangeAddOnVariantId: z.string().optional(),
  carryOverLineItemProperties: z.boolean().optional(),
});

/** Projection of Step.SubmitWarranty["option"]; parse() also strips unread fields. */
export const SubmitWarrantyOptionViewSchema = z.object({
  method: ReturnOptionMethodZodSchema,
  carryOverLineItemProperties: z.boolean().optional(),
  allowUnpublishedReplacement: z.boolean().optional(),
});

const FlowStepViewSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("multiple_choice"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    message: z.string(),
    description: z.string().optional(),
    setsReason: z.boolean(),
    shuffle: z.boolean(),
    options: z.array(z.object({ index: z.number().int(), name: z.string() })),
  }),
  z.object({
    type: z.literal("input"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    // NOTE: parts[].exampleImage is a model URL that serializes to a bare string; moot for POS (image inputs unsupported).
    parts: z.array(z.custom<Step.InputPart>()),
  }),
  z.object({
    type: z.literal("information"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    header: z.string(),
    message: z.string(),
    continueButtonText: z.string(),
    terminal: z.boolean(),
  }),
  z.object({
    type: z.literal("reject"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    message: z.string(),
  }),
  z.object({
    type: z.literal("block"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    message: z.string(),
  }),
  z.object({
    type: z.literal("ai_return_reasons"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    next: z.number().int(),
    nextDamaged: z.number().int().optional(),
    nextWrongProduct: z.number().int().optional(),
    nextExchange: z.number().int().optional(),
    customGuidance: z.array(aiCustomGuidanceSchema).optional(),
    customQuestion: z.string().optional(),
    customExplanationSubtext: z.string().optional(),
    merchantGuidance: z.string().optional(),
    additionalAiInfo: z.string().optional(),
    maxQuestions: z.number().int().optional(),
  }),
  z.object({
    type: z.literal("return"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    options: z.array(ReturnStepPageOptionSchema),
  }),
  z.object({
    type: z.literal("submit_claim"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    option: SubmitClaimOptionViewSchema,
  }),
  z.object({
    type: z.literal("submit_repair"),
    stepId: z.number().int(),
    flowPath: flowPathField,
  }),
  z.object({
    type: z.literal("submit_warranty"),
    stepId: z.number().int(),
    flowPath: flowPathField,
    option: SubmitWarrantyOptionViewSchema,
  }),
  z.object({
    type: z.literal("done"),
    stepId: z.number().int(),
    flowPath: flowPathField,
  }),
]);

/**
 * getNextFlowStep view types, derived from the zod schemas above — the schema
 * is the single source of truth for the wire shape.
 */
export type FlowStepView = z.infer<typeof FlowStepViewSchema>;

export type MultipleChoiceStepView = Extract<
  FlowStepView,
  { type: "multiple_choice" }
>;
export type InputStepView = Extract<FlowStepView, { type: "input" }>;
export type InformationStepView = Extract<
  FlowStepView,
  { type: "information" }
>;
export type RejectStepView = Extract<FlowStepView, { type: "reject" }>;
export type BlockStepView = Extract<FlowStepView, { type: "block" }>;
export type AiReturnReasonsStepView = Extract<
  FlowStepView,
  { type: "ai_return_reasons" }
>;
export type ReturnStepView = Extract<FlowStepView, { type: "return" }>;
export type SubmitClaimStepView = Extract<
  FlowStepView,
  { type: "submit_claim" }
>;
export type SubmitRepairStepView = Extract<
  FlowStepView,
  { type: "submit_repair" }
>;
export type SubmitWarrantyStepView = Extract<
  FlowStepView,
  { type: "submit_warranty" }
>;
export type DoneStepView = Extract<FlowStepView, { type: "done" }>;

export type SubmitClaimOptionView = z.infer<typeof SubmitClaimOptionViewSchema>;
export type SubmitWarrantyOptionView = z.infer<
  typeof SubmitWarrantyOptionViewSchema
>;

export const getNextFlowStep = {
  name: "getNextFlowStep" as const,
  input: z.object({
    draftReturnId: z.string(),
    /**
     * Which returnable item's context to evaluate per-item conditions against.
     * Omit when the draft return has a single item; required to disambiguate.
     */
    returnableItemId: z.string().optional(),
    /** Omit for the first step of the flow. */
    currentStepId: z.number().int().optional(),
    /** Override the start index of the initial walk; else the handler resolves the product reset step. */
    startStepId: z.number().int().optional(),
    /** Required when currentStepId is set. */
    answer: FlowAnswerSchema.optional(),
  }),
  output: z.object({ step: FlowStepViewSchema }),
};
